import React, { useState, useEffect, useRef, useCallback } from 'react';
import TopBar from '../../Components/Games/TopBar.jsx';
import ResultPage from '../../Components/Games/ResultPage.jsx';
import StartGame from '../../Components/Games/StartGame.jsx';
import GameIntroScreen from '../../Components/Games/GameIntroScreen.jsx';
import overlay from '../../assets/img/overlay.png';
import signalScrambleIcon from '../../assets/img/icon-Signal-Scramble.png';
import './SignalScramble.css';
import { lifestyleApi } from '../../apiService/api';
import { getUrlParams } from '../../apiService/timespam';
import { useGameBackNavigation } from "../../Components/Games/useGameBackNavigation";


const DIRECTIONS = ['up', 'down', 'left', 'right'];
const COLORS = ['green', 'red'];
const OFFSETS = ['left-side', 'center-side', 'right-side'];

const SignalArrow = ({ direction, color }) => (
    <div className={`signal-arrow ${color} ${direction}`}>
        <svg viewBox="0 0 100 100"><path d="M50 5 L90 45 L70 45 L70 95 L30 95 L30 45 L10 45 Z" /></svg>
    </div>
);

const SignalScramble = () => {
    useGameBackNavigation();
    const [screen, setScreen] = useState("intro");
    const [gameState, setGameState] = useState('START');
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(60);
    const [correctCount, setCorrectCount] = useState(0);
    const [wrongCount, setWrongCount] = useState(0);
    const [fastResponses, setFastResponses] = useState(0); // Track fast swipes
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [bestScore, setBestScore] = useState(() => parseInt(localStorage.getItem('scramble_best')) || 0);
    const [currentArrow, setCurrentArrow] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [feedbackMsg, setFeedbackMsg] = useState("");
    const [isTutorial, setIsTutorial] = useState(false);
    const [tutorialStep, setTutorialStep] = useState(1);
    const [cid, setCid] = useState("");
    const [groupName, setGroupName] = useState("");
    const [gameStateApi, setGameStateApi] = useState("initial");
    const [lastInsertedId, setLastInsertedId] = useState(null);
    const [goqiiBest, setGoqiiBest] = useState(0);
    const [backendCognitiveScore, setBackendCognitiveScore] = useState(null);
    const [backendSkill, setBackendSkill] = useState("");
    const [weightages, setWeightages] = useState({ attemptWeightage: 1, accuracyWeightage: 1, streakWeightage: 1, fastWeightage: 1, scoreWeightage: 1, baseQuestionsAttempt: 0, correctMinQuestion: 1, streakMinQuestion: 1, fastResponseMinQuestion: 1, scorePoints: 1 });
    const [configError, setConfigError] = useState(false);



    const inputStartPos = useRef(null);
    const timerRef = useRef(null);
    const responseStartTime = useRef(null); // Ref to track response speed
    const lastArrowRef = useRef(null);
    const lastInsertedIdRef = useRef(null);


    const generateArrow = useCallback(() => {
        let newDirection, newColor, newOffset;

        // Loop until we get a combination that is different from the last one
        do {
            newDirection = DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)];
            newOffset = OFFSETS[Math.floor(Math.random() * OFFSETS.length)];
            newColor = COLORS[Math.floor(Math.random() * COLORS.length)];
        } while (
            lastArrowRef.current &&
            newDirection === lastArrowRef.current.direction &&
            newOffset === lastArrowRef.current.offset
        );

        const nextArrow = {
            direction: newDirection,
            color: newColor,
            offset: newOffset,
            id: Date.now()
        };

        lastArrowRef.current = nextArrow; // Save this for the next round
        setCurrentArrow(nextArrow);
        responseStartTime.current = Date.now();
    }, []);
    const fetchGameConfig = () => {
        setConfigError(false);
        const { encryptedId, gid } = getUrlParams();

        lifestyleApi.fetchGameDetails(encryptedId, gid)
            .then(res => {
                if (res.data?.code === 200) {
                    const d = res.data.data;
                    setBestScore(d.bestScore);
                    setGoqiiBest(d.goqiiBest);
                    setWeightages({
                        attemptWeightage: d.attemptWeightage ?? 1,
                        accuracyWeightage: d.accuracyWeightage ?? 1,
                        streakWeightage: d.streakWeightage ?? 1,
                        fastWeightage: d.fastWeightage ?? 1,
                        scoreWeightage: d.scoreWeightage ?? 1,
                        baseQuestionsAttempt: d.baseQuestionsAttempt || 0,
                        correctMinQuestion: d.correctMinQuestion || 1,
                        streakMinQuestion: d.streakMinQuestion || 1,
                        fastResponseMinQuestion: d.fastResponseMinQuestion || 1,
                        scorePoints: d.scorePoints || 1,
                    });
                } else {
                    setConfigError(true);
                }
            })
            .catch(err => {
                console.error("fetchGameDetails failed", err);
                setConfigError(true);
            });
    };

    useEffect(() => {
        fetchGameConfig();
    }, []);

    // --- FIX: Proper Reset Function for Replay ---
    const resetGame = async () => {
        const { encryptedId, gid } = getUrlParams();

        try {
            const res = await lifestyleApi.initialGameScore(encryptedId, gid);

            if (res.data?.code === 200) {
                const data = res.data.data;

                setCid(data.cid);
                setGroupName(data.groupName);
                setLastInsertedId(data.lastInsertedId);
                lastInsertedIdRef.current = data.lastInsertedId;
                setGameStateApi("initial");
            }
        } catch (err) {
            console.error("initialGameScore failed", err);
        }
        clearInterval(timerRef.current);
        setScore(0);
        setTimeLeft(60);
        setCorrectCount(0);
        setWrongCount(0);
        setFastResponses(0);
        setStreak(0);
        setBestStreak(0);
        setFeedback(null);
        setIsTutorial(false);
        setGameState('PLAYING');
        generateArrow();
        startTimer();
    };
    const handleReplay = () => {
        // 1. Clear stats immediately so the background looks fresh
        setScore(0);
        setTimeLeft(60);
        setCorrectCount(0);
        setWrongCount(0);
        setFastResponses(0);
        setStreak(0);
        setFeedback(null);
        setCurrentArrow(null); // Hide the previous arrow

        // 2. Show the "READY" (START!) overlay
        setGameState('READY');

        // 3. Wait 1.5 seconds (to show START!) then trigger the real reset
        setTimeout(() => {
            resetGame();
        }, 1500);
    };

    const startTimer = () => {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    setGameState('GAMEOVER');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const validateMove = useCallback((inputDir) => {
        if (gameState !== 'PLAYING' || feedback || !currentArrow) return;

        // Calculate Speed
        const reactionTime = Date.now() - responseStartTime.current;

        const isRed = currentArrow.color === 'red';
        let requiredDir = currentArrow.direction;
        if (isRed) {
            const opposites = { up: 'down', down: 'up', left: 'right', right: 'left' };
            requiredDir = opposites[requiredDir];
        }

        const isCorrect = inputDir === requiredDir;

        if (isTutorial) {
            if (isCorrect) {
                setFeedback('correct');
                setFeedbackMsg("GREAT!");
                setTimeout(() => {
                    setFeedback(null);
                    setFeedbackMsg("");
                    if (tutorialStep === 1) {
                        setTutorialStep(2);
                        // Manually set next tutorial arrow
                        const tut2 = { direction: 'left', color: 'red', offset: 'center-side', id: 'tut2' };
                        lastArrowRef.current = tut2;
                        setCurrentArrow(tut2);

                    } else {
                        transitionToRealGame();
                    }
                }, 800);
            } else {
                setFeedback('wrong');
                setFeedbackMsg("OOPS!");
                setTimeout(() => {
                    setFeedback(null);
                    setFeedbackMsg("");
                }, 500);
            }
            return;
        }

        if (isCorrect) {
            setFeedback('correct');
            const msgs = ["GREAT!", "PERFECT!", "FAST!"];
            setFeedbackMsg(msgs[Math.floor(Math.random() * msgs.length)]);
            // Track fast responses (e.g., swiped within 800ms)
            let fastBonus = 0;
            if (reactionTime < 800) {
                setFastResponses(prev => prev + 1);
                fastBonus = weightages.fastWeightage * 10;
            }

            // streak here is the streak count BEFORE this correct answer
            const streakBonus = weightages.streakWeightage * 10 * streak;
            const gained = weightages.scorePoints + streakBonus + fastBonus;
            const newScore = Math.max(0, score + gained);
            setScore(newScore);
            setCorrectCount(prev => prev + 1);
            setStreak(prev => {
                const ns = prev + 1;
                if (ns > bestStreak) setBestStreak(ns);
                return ns;
            });
            if (newScore > bestScore) {
                setBestScore(newScore);
                localStorage.setItem('scramble_best', newScore.toString());
            }
        } else {
            setFeedback('wrong');
            setFeedbackMsg("OOPS!"); // Negative message
            setWrongCount(prev => prev + 1);
            setStreak(0);
            // A wrong action must never decrease the round countdown timer.
            setScore(prev => Math.max(0, prev - weightages.scorePoints / 2));
        }

        setTimeout(() => {
            setFeedback(null);
            setFeedbackMsg("");
            generateArrow();
        }, 400);
    }, [gameState, feedback, currentArrow, isTutorial, tutorialStep, streak, score, bestStreak, bestScore, generateArrow, weightages]);

    // Keyboard & Swipe handlers... (Keeping your existing logic)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
                e.preventDefault();
                validateMove(e.key.replace("Arrow", "").toLowerCase());
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [validateMove]);

    const onInputStart = (x, y) => { inputStartPos.current = { x, y }; };
    const onInputEnd = (endX, endY) => {
        if (!inputStartPos.current) return;
        const dx = endX - inputStartPos.current.x;
        const dy = endY - inputStartPos.current.y;
        inputStartPos.current = null;
        if (Math.max(Math.abs(dx), Math.abs(dy)) > 30) {
            if (Math.abs(dx) > Math.abs(dy)) validateMove(dx > 0 ? 'right' : 'left');
            else validateMove(dy > 0 ? 'down' : 'up');
        }
    };

    const startTutorial = () => {
        setIsTutorial(true); setScreen("game"); setGameState('PLAYING'); setTutorialStep(1);
        setCurrentArrow({ direction: 'up', color: 'green', offset: 'center-side', id: 'tut1' });
    };

    const transitionToRealGame = () => {
        setIsTutorial(false);
        setGameState('READY');
        setTimeout(() => {
            resetGame(); // Use the reset function to ensure timer starts at 60
        }, 1500);
    };

    useEffect(() => { return () => clearInterval(timerRef.current); }, []);

    const saveFinalGameScore = async () => {
        const { encryptedId, gid } = getUrlParams();
        const goqiiUserId = encryptedId;
        const totalQuestions = correctCount + wrongCount;

        const payload = {
            goqiiUserId,
            gid,
            totalQuestions,
            correctAnswers: correctCount,
            wrongAnswers: wrongCount,
            longestStreak: bestStreak,
            fastResponse: fastResponses,
            score,
            cid,
            lastInsertedId: lastInsertedIdRef.current,
        };

        try {
            const response = await lifestyleApi.saveGameScore(
                payload.goqiiUserId,
                payload.gid,
                payload.totalQuestions,
                payload.correctAnswers,
                payload.wrongAnswers,
                payload.longestStreak,
                payload.fastResponse,
                payload.score,
                payload.cid,
                payload.lastInsertedId
            );
            if (response?.data?.data?.cognitiveScore !== undefined) {
                setBackendCognitiveScore(response.data.data.cognitiveScore);
            }

            if (response?.data?.data?.skill) {
                setBackendSkill(response.data.data.skill);
            }
        } catch (err) {
            console.error("saveGameScore failed", err);
        }
    };
    useEffect(() => {
        if (gameState === "GAMEOVER") {
            saveFinalGameScore();
        }
    }, [gameState]);

    const getQuote = () => {
        if (backendCognitiveScore >= 90) return "Elite cognitive control! Lightning-fast decisions.";
        if (backendCognitiveScore >= 75) return "Great focus! Your reflexes are sharp.";
        if (backendCognitiveScore >= 60) return "Good control. Keep training your mind.";
        if (backendCognitiveScore >= 40) return "Nice effort. Consistency builds mastery.";
        return "Every swipe trains your brain. Try again!";
    };



    return (
        <div className={`shape-shift-wrapper signal-scramble ${feedback}`}>
            {configError && (
                <div className="api-error-overlay">
                    <div className="api-error-card">
                        <div className="api-error-title">Something went wrong</div>
                        <div className="api-error-text">There is a server error to open this game. Please try again after some time.</div>
                        <button className="api-error-retry-btn" onClick={fetchGameConfig}>Retry</button>
                    </div>
                </div>
            )}

            {screen === "game" && <TopBar score={score} timeLeft={isTutorial ? "∞" : timeLeft} />}

            <div className="main-area">
                {screen === "intro" ? (
                    <GameIntroScreen backgroundImage={overlay} gameLogo={signalScrambleIcon} title="Signal Scramble" onStart={() => setScreen("start")} />
                ) : gameState === 'START' ? (
                    <StartGame backgroundImage={overlay}
                        // hbTitle="Inhibitory Control" 
                        hbHeading="Inhibition Control"
                        hbDescription="Training your brain to stop and think improves focus, self-control, and quick decision-making."
                        yourBest={bestScore}
                        goqiiBest={goqiiBest}
                        onStart={startTutorial} />
                ) : gameState === 'GAMEOVER' ? (
                    <ResultPage
                        visible={true}
                        fastResponses={fastResponses}
                        score={score}
                        best={bestScore}
                        correct={correctCount}
                        wrong={wrongCount}
                        longestStreak={bestStreak}
                        domainLabel={backendSkill}
                        quote={getQuote()}
                        cognitiveScore={backendCognitiveScore} 
                        onReplay={handleReplay} 
                    />
                ) : (
                    <div className={`game-board ${isTutorial ? 'tutorial-bg' : ''}`}>
                        {feedbackMsg && (
                            <div className={`feedback-popup-text ${feedback}`}>
                                {feedbackMsg}
                            </div>
                        )}
                        {/* Tutorial UI Code ... (your existing responsive code) */}
                        {isTutorial && (
                            <div className="tutorial-overlay-content">
                                <div className="tutorial-top-row">
                                    <div className="tutorial-badge">Tutorial</div>
                                    <button className="tutorial-skip-btn" onClick={transitionToRealGame}>Skip ⏩</button>
                                </div>
                                <div className="mission-briefing-ui">
                                    <div className="briefing-cards">
                                        <div className="brief-card green-card">
                                            <div className="brief-icon green-bg">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M12 19V5M12 5L5 12M12 5L19 12" /></svg>
                                            </div>
                                            <h3>GREEN SIGNAL</h3>
                                            <p>Swipe <span className="highlight-green">SAME</span> direction.</p>
                                        </div>
                                        <div className="brief-card red-card">
                                            <div className="brief-icon red-bg">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                            </div>
                                            <h3>RED SIGNAL</h3>
                                            <p>Swipe <span className="highlight-red">OPPOSITE</span> direction.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {gameState === 'READY' && <div className="ready-overlay"><div className="ready-text">START!</div></div>}

                        <div className="scramble-ui">
                            <div className="instruction-text">
                                {isTutorial
                                    ? (tutorialStep === 1 ? "SWIPE UP" : "REVERSE! SWIPE RIGHT")
                                    : (<div className="instruction-text">
                                        MATCH THE SIGNAL RULE
                                    </div>)
                                    // (currentArrow?.color === 'green' ? "FOLLOW" : "REVERSE")
                                }
                            </div>
                            <div className={`arrow-play-zone ${currentArrow?.offset}`}
                                onMouseDown={(e) => onInputStart(e.clientX, e.clientY)}
                                onMouseUp={(e) => onInputEnd(e.clientX, e.clientY)}
                                onTouchStart={(e) => onInputStart(e.touches[0].clientX, e.touches[0].clientY)}
                                onTouchEnd={(e) => onInputEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY)}
                            >
                                {currentArrow && <SignalArrow {...currentArrow} />}
                            </div>
                            {/* {!isTutorial && <div className="streak-tag">STREAK: {streak}</div>} */}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SignalScramble;